import { IncludeTestToggle } from "./TestAccounts";
import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { API, money, th, td, inp, StatPill, Empty, qtyLabel } from "./shared";

// =========================================================================
// ADMIN FIX JOB OWNERSHIP — repair tool for jobs filed under the wrong buyer_id
// (e.g. legacy jobs stamped against the hardcoded "buyer-1" demo account).
// Admin enters job id + target buyer email → one-click transfer + audit entry.
// =========================================================================
export function AcFixJobOwnership() {
  const [jobQuery, setJobQuery] = useState("");
  const [jobMatches, setJobMatches] = useState([]);
  const [job, setJob] = useState(null);
  const [email, setEmail] = useState("");
  const [matches, setMatches] = useState([]);
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const searchJob = async () => {
    setErr(""); setJob(null); setJobMatches([]);
    if (!jobQuery.trim()) return;
    try {
      const { data } = await axios.get(`${API}/admin/jobs/search?q=${encodeURIComponent(jobQuery.trim())}`);
      if (!data.items?.length) {
        setErr(`No jobs match "${jobQuery.trim()}". Try a different keyword or the full job id.`);
        return;
      }
      if (data.items.length === 1) {
        setJob(data.items[0]);
      } else {
        setJobMatches(data.items);
      }
    } catch (e) { setErr(e?.response?.data?.detail || "Search failed"); }
  };

  const findBuyer = async () => {
    setErr(""); setMatches([]); setPicked(null);
    if (!email.trim()) return;
    try {
      const { data } = await axios.get(`${API}/admin/users/lookup?email=${encodeURIComponent(email.trim())}`);
      if (!data.users?.length) {
        const { data: byName } = await axios.get(`${API}/admin/users/lookup?name=${encodeURIComponent(email.trim())}`);
        setMatches(byName.users || []);
      } else {
        setMatches(data.users);
      }
    } catch (e) { setErr(e?.response?.data?.detail || "Lookup failed"); }
  };

  const transfer = async () => {
    if (!job || !picked) return;
    if (!window.confirm(`Transfer ${job.id} from ${job.buyer_id} to ${picked.email} (${picked.id})? Writes an audit entry you can undo.`)) return;
    setBusy(true); setErr("");
    try {
      await axios.patch(`${API}/admin/jobs/${job.id}/transfer-owner`, { new_buyer_id: picked.id });
      toast.success(`Transferred — ${picked.email} can now award this job.`);
      // Reload the job so the UI shows the new owner
      try {
        const { data } = await axios.get(`${API}/jobs/${job.id}`);
        setJob(data);
      } catch { /* ignore */ }
    } catch (e) { setErr(e?.response?.data?.detail || "Transfer failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="ac-fix-ownership" style={{marginTop:20,maxWidth:720}}>
      <div style={{background:"#FEF3C7",border:"1px solid #FDE68A",borderRadius:10,padding:"12px 14px",marginBottom:16,fontSize:12,color:"#92400E"}}>
        <b>When to use this:</b> if a buyer reports "Only the buyer or admin can award & fund escrow", their job is probably stamped against the wrong <code>buyer_id</code> (legacy hardcoded <code>buyer-1</code> bug). Transfer the job to the real buyer here. The transfer is written to the Audit tab so you can undo.
      </div>

      {err && <div style={{background:"#FEF2F2",border:"1px solid #FECACA",color:"#991B1B",padding:"8px 12px",borderRadius:8,fontSize:12,marginBottom:12}}>{err}</div>}

      <div style={{background:"#fff",border:"1px solid #E5E7EB",borderRadius:12,padding:16,marginBottom:14}}>
        <div style={{fontSize:11,fontWeight:700,color:"#6B7280",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:6}}>Step 1 · Look up the job</div>
        <div style={{fontSize:11,color:"#6B7280",marginBottom:8}}>Paste the full <code>job-xxxxxxxx</code> id, or search by title keyword (e.g. <code>brochure</code>, <code>pouch</code>).</div>
        <div style={{display:"flex",gap:8}}>
          <input data-testid="fix-job-id" value={jobQuery} onChange={e => setJobQuery(e.target.value)} onKeyDown={e => { if (e.key === "Enter") searchJob(); }} placeholder="job-xxxxxxxx or keyword" style={{flex:1,padding:"8px 10px",border:"1px solid #D1D5DB",borderRadius:8,fontSize:13}}/>
          <button data-testid="fix-job-lookup" onClick={searchJob} style={{background:"#111827",color:"#FACC15",border:"none",padding:"8px 16px",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>Load</button>
        </div>
        {jobMatches.length > 0 && !job && (
          <div style={{marginTop:10,display:"grid",gap:6}}>
            <div style={{fontSize:11,color:"#6B7280"}}>{jobMatches.length} match{jobMatches.length === 1 ? "" : "es"} — pick one:</div>
            {jobMatches.map(j => (
              <button key={j.id} data-testid={`fix-job-match-${j.id}`} onClick={() => { setJob(j); setJobMatches([]); }} style={{textAlign:"left",background:"#F9FAFB",border:"1px solid #E5E7EB",padding:"10px 12px",borderRadius:8,cursor:"pointer"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <b style={{fontSize:13}}>{j.title}</b>
                  <span style={{background:"#F3F4F6",padding:"2px 8px",borderRadius:999,fontSize:10,fontWeight:700}}>{j.status}</span>
                </div>
                <div style={{fontSize:11,color:"#6B7280",marginTop:3}}>Owner: <code>{j.buyer_id}</code> · Qty {j.quantity} · {j.bid_count || 0} bid{j.bid_count === 1 ? "" : "s"}</div>
                <div style={{fontSize:10,color:"#9CA3AF",fontFamily:"'DM Mono', monospace",marginTop:2}}>{j.id}</div>
              </button>
            ))}
          </div>
        )}
        {job && (
          <div style={{marginTop:10,background:"#F0FDF4",border:"2px solid #10B981",borderRadius:8,padding:"10px 12px",fontSize:12}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <b>{job.title}</b>
              <button onClick={() => { setJob(null); setJobMatches([]); }} style={{background:"transparent",border:"none",color:"#2563EB",fontSize:11,cursor:"pointer",fontWeight:600}}>Change job</button>
            </div>
            <div style={{color:"#6B7280",marginTop:4}}><span style={{background:"#F3F4F6",padding:"2px 8px",borderRadius:999,fontSize:10}}>{job.status}</span> · <code>{job.id}</code> · Qty {qtyLabel(job)} · {job.bid_count || 0} bid{job.bid_count === 1 ? "" : "s"}</div>
            <div style={{color:"#6B7280",marginTop:4}}>Current owner: <code>{job.buyer_id}</code></div>
          </div>
        )}
      </div>

      <div style={{background:"#fff",border:"1px solid #E5E7EB",borderRadius:12,padding:16,marginBottom:14}}>
        <div style={{fontSize:11,fontWeight:700,color:"#6B7280",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:6}}>Step 2 · Find the real buyer</div>
        <div style={{display:"flex",gap:8}}>
          <input data-testid="fix-buyer-query" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => { if (e.key === "Enter") findBuyer(); }} placeholder="email or name" style={{flex:1,padding:"8px 10px",border:"1px solid #D1D5DB",borderRadius:8,fontSize:13}}/>
          <button data-testid="fix-buyer-lookup" onClick={findBuyer} style={{background:"#111827",color:"#FACC15",border:"none",padding:"8px 16px",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>Search</button>
        </div>
        {matches.length > 0 && (
          <div style={{marginTop:10,display:"grid",gap:6}}>
            {matches.map(u => (
              <button key={u.id} data-testid={`fix-buyer-${u.id}`} onClick={() => setPicked(u)} style={{textAlign:"left",background: picked?.id === u.id ? "#F0FDF4" : "#F9FAFB", border: picked?.id === u.id ? "2px solid #10B981" : "1px solid #E5E7EB", padding:"10px 12px",borderRadius:8,cursor:"pointer"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div>
                    <b style={{fontSize:13}}>{u.name || "Unnamed"}</b> <span style={{fontSize:11,color:"#6B7280"}}>· {u.email}</span>
                  </div>
                  <span style={{background:"#F3F4F6",padding:"2px 8px",borderRadius:999,fontSize:10,fontWeight:700}}>{u.role}</span>
                </div>
                <div style={{fontSize:10,color:"#9CA3AF",fontFamily:"'DM Mono', monospace",marginTop:3}}>{u.id}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div style={{background:"#fff",border:"1px solid #E5E7EB",borderRadius:12,padding:16}}>
        <div style={{fontSize:11,fontWeight:700,color:"#6B7280",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:6}}>Step 3 · Transfer</div>
        <div style={{fontSize:12,color:"#6B7280",marginBottom:10}}>
          {!job ? "Load a job first." : !picked ? "Pick a buyer first." : <>Will transfer <b>{job.id}</b> from <code>{job.buyer_id}</code> → <code>{picked.id}</code> ({picked.email}).</>}
        </div>
        <button data-testid="fix-transfer" disabled={!job || !picked || busy} onClick={transfer} style={{background: (!job||!picked||busy) ? "#D1D5DB" : "#111827", color: (!job||!picked||busy) ? "#6B7280" : "#FACC15", border:"none", padding:"10px 20px", borderRadius:8, fontSize:13, fontWeight:700, cursor: (!job||!picked||busy) ? "not-allowed" : "pointer"}}>{busy ? "Transferring…" : "Transfer ownership"}</button>
      </div>
    </div>
  );
}


// =========================================================================
// ADMIN AUDIT LOG — read-only ledger with filters + one-click undo
// =========================================================================
export function AcAuditLog() {
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ target_type: "", action: "", days: 30 });
  const [busyId, setBusyId] = useState("");

  const load = async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
    try {
      const { data } = await axios.get(`${API}/admin/audit?${params.toString()}`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load audit log");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.target_type, filters.action, filters.days]);

  const undo = async (id) => {
    if (!window.confirm("Restore the 'before' snapshot of this action onto its target? The undo is also audited.")) return;
    setBusyId(id);
    try {
      await axios.post(`${API}/admin/audit/${id}/undo`);
      toast.success("Undo applied");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Undo failed");
    } finally { setBusyId(""); }
  };

  if (!data) return <Empty text="Loading audit log…"/>;
  const TARGETS = ["", "sample_pack", "settlement", "agency_profile", "agency_featured", "partner_app"];
  const ACTIONS = ["", "sample_pack_patch", "agency_featured_toggle", "settlement_authorize", "partner_review", "undo"];

  return (
    <div data-testid="ac-audit" style={{marginTop:20}}>
      <div style={{display:"flex",gap:12,marginBottom:14,flexWrap:"wrap"}}>
        <div style={{background:"#F9FAFB",border:"1px solid #E5E7EB",borderRadius:10,padding:"10px 14px",minWidth:160}}>
          <div style={{fontSize:10,color:"#6B7280",textTransform:"uppercase",letterSpacing:"0.08em",fontWeight:700}}>Entries shown</div>
          <div style={{fontSize:18,fontWeight:700,color:"#111827",marginTop:3}}>{data.totals.count}</div>
        </div>
        <div style={{background:"#F9FAFB",border:"1px solid #E5E7EB",borderRadius:10,padding:"10px 14px",minWidth:160}}>
          <div style={{fontSize:10,color:"#6B7280",textTransform:"uppercase",letterSpacing:"0.08em",fontWeight:700}}>Distinct actors</div>
          <div style={{fontSize:18,fontWeight:700,color:"#111827",marginTop:3}}>{data.totals.actors}</div>
        </div>
        <div style={{background:"#F9FAFB",border:"1px solid #E5E7EB",borderRadius:10,padding:"10px 14px",minWidth:160}}>
          <div style={{fontSize:10,color:"#6B7280",textTransform:"uppercase",letterSpacing:"0.08em",fontWeight:700}}>Window</div>
          <div style={{fontSize:18,fontWeight:700,color:"#111827",marginTop:3}}>Last {data.days} days</div>
        </div>
      </div>

      <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap"}}>
        <select data-testid="audit-target-filter" value={filters.target_type} onChange={e => setFilters({...filters, target_type: e.target.value})} style={{padding:"6px 10px",border:"1px solid #D1D5DB",borderRadius:8,fontSize:12}}>
          {TARGETS.map(t => <option key={t || "all"} value={t}>{t ? `target: ${t}` : "All targets"}</option>)}
        </select>
        <select data-testid="audit-action-filter" value={filters.action} onChange={e => setFilters({...filters, action: e.target.value})} style={{padding:"6px 10px",border:"1px solid #D1D5DB",borderRadius:8,fontSize:12}}>
          {ACTIONS.map(a => <option key={a || "all"} value={a}>{a ? `action: ${a}` : "All actions"}</option>)}
        </select>
        <select data-testid="audit-days-filter" value={filters.days} onChange={e => setFilters({...filters, days: Number(e.target.value)})} style={{padding:"6px 10px",border:"1px solid #D1D5DB",borderRadius:8,fontSize:12}}>
          {[1, 7, 14, 30, 60, 90, 180, 365].map(d => <option key={d} value={d}>Last {d} days</option>)}
        </select>
      </div>

      {(data.items || []).length === 0 ? (
        <Empty text="No audit records match that filter."/>
      ) : (
        <div style={{display:"grid",gap:8}}>
          {data.items.map(a => (
            <div key={a.id} data-testid={`audit-row-${a.id}`} style={{border:"1px solid #E5E7EB",borderRadius:10,padding:"10px 14px",background:"#fff",display:"grid",gridTemplateColumns:"1fr auto",gap:10,alignItems:"start"}}>
              <div style={{minWidth:0}}>
                <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:4,flexWrap:"wrap"}}>
                  <span style={{background:"#111827",color:"#FACC15",padding:"2px 8px",borderRadius:999,fontSize:10,fontWeight:800,letterSpacing:"0.06em"}}>{a.action}</span>
                  <span style={{background:"#F3F4F6",padding:"2px 8px",borderRadius:999,fontSize:10,fontWeight:700,color:"#374151"}}>{a.target_type}</span>
                  <span style={{fontSize:11,color:"#9CA3AF",fontFamily:"'DM Mono', monospace"}}>#{(a.target_id || "").slice(-8)}</span>
                  <span style={{fontSize:11,color:"#6B7280"}}>· by {a.actor_name || a.actor_id?.slice(-6) || "system"}</span>
                  <span style={{fontSize:11,color:"#9CA3AF",marginLeft:"auto"}}>{new Date(a.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</span>
                </div>
                {(a.before || a.after) && (
                  <details style={{marginTop:6}}>
                    <summary style={{fontSize:11,color:"#2563EB",cursor:"pointer",userSelect:"none"}}>Show diff</summary>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginTop:6}}>
                      <pre style={{background:"#FEF2F2",padding:8,borderRadius:6,fontSize:10,overflow:"auto",margin:0,maxHeight:140}}>Before:
{JSON.stringify(a.before || {}, null, 2)}</pre>
                      <pre style={{background:"#F0FDF4",padding:8,borderRadius:6,fontSize:10,overflow:"auto",margin:0,maxHeight:140}}>After:
{JSON.stringify(a.after || {}, null, 2)}</pre>
                    </div>
                  </details>
                )}
                {a.meta?.changes ? <div style={{fontSize:11,color:"#6B7280",marginTop:4}}>Changes: <code>{JSON.stringify(a.meta.changes)}</code></div> : null}
              </div>
              {a.before && a.action !== "undo" ? (
                <button data-testid={`audit-undo-${a.id}`} disabled={busyId === a.id} onClick={() => undo(a.id)} style={{background: busyId === a.id ? "#9CA3AF" : "#F59E0B", color:"#111827", border:"none", padding:"7px 14px", borderRadius:8, fontSize:11, fontWeight:800, cursor:"pointer", height:28}}>↶ Undo</button>
              ) : <span style={{fontSize:11,color:"#9CA3AF"}}>—</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// =========================================================================
// ADMIN SAMPLE PACKS — curate, mark shipped, update status, log history
// =========================================================================
export function AcSamplePacks() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    try {
      const { data } = await axios.get(`${API}/admin/sample-packs?${params.toString()}`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load sample packs");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  if (!data) return <Empty text="Loading sample packs…"/>;
  const STATUS_OPTS = ["", "pending_quote", "quoted", "paid", "curating", "shipped", "delivered", "rated", "cancelled"];

  return (
    <div data-testid="ac-sample-packs" style={{marginTop:20}}>
      <div style={{display:"flex",gap:12,marginBottom:14,flexWrap:"wrap"}}>
        {[
          { label: "Total paid", value: data.paid_count },
          { label: "Revenue · INR", value: `₹${(data.revenue_inr || 0).toLocaleString("en-IN")}` },
          { label: "In curation", value: data.totals?.curating || 0 },
          { label: "Shipped", value: data.totals?.shipped || 0 },
          { label: "Delivered + rated", value: (data.totals?.delivered || 0) + (data.totals?.rated || 0) },
        ].map(p => (
          <div key={p.label} style={{background:"#F9FAFB",border:"1px solid #E5E7EB",borderRadius:10,padding:"10px 14px",flex:"1 1 160px"}}>
            <div style={{fontSize:10,color:"#6B7280",textTransform:"uppercase",letterSpacing:"0.08em",fontWeight:700}}>{p.label}</div>
            <div style={{fontSize:18,fontWeight:700,color:"#111827",marginTop:3}}>{p.value}</div>
          </div>
        ))}
      </div>
      <div style={{display:"flex",gap:6,marginBottom:12,flexWrap:"wrap"}}>
        {STATUS_OPTS.map(s => (
          <button key={s || "all"} onClick={() => setStatus(s)} style={{background: status === s ? "#111827" : "#F3F4F6", color: status === s ? "#FACC15" : "#374151", border:"none", padding:"6px 12px", borderRadius:999, fontSize:11, fontWeight:700, cursor:"pointer"}}>{s || "All"} {s && `· ${data.totals?.[s] || 0}`}</button>
        ))}
      </div>
      {(data.items || []).length === 0 ? (
        <Empty text="No sample packs match that filter."/>
      ) : (
        <div style={{display:"grid",gap:10}}>
          {data.items.map(sp => (
            <div key={sp.id} data-testid={`ac-sp-${sp.id}`} style={{border:"1px solid #E5E7EB",borderRadius:10,padding:"12px 14px",background:"#fff",display:"grid",gridTemplateColumns:"1fr auto",gap:10,alignItems:"center"}}>
              <div style={{minWidth:0}}>
                <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:4}}>
                  <span style={{background:"#F3F4F6",padding:"2px 8px",borderRadius:999,fontSize:10,fontWeight:700,textTransform:"uppercase"}}>{sp.status}</span>
                  <b style={{fontSize:14}}>{sp.product_category}</b>
                  <span style={{fontSize:11,color:"#9CA3AF"}}>#{sp.id.slice(-6)}</span>
                </div>
                <div style={{fontSize:12,color:"#6B7280"}}>{sp.buyer_name} · ₹{(sp.fee_inr || 0).toLocaleString("en-IN")} · {sp.payment_status} · {sp.ship_city}, {sp.ship_state}</div>
                <div style={{fontSize:11,color:"#9CA3AF",marginTop:4}}>Substrates: {(sp.substrate_preferences || []).join(", ") || "—"} · Finishes: {(sp.finish_preferences || []).join(", ") || "—"} · {(sp.requested_printer_ids || []).length} printer(s) picked</div>
                {sp.courier && sp.tracking_number ? <div style={{fontSize:11,color:"#065F46",marginTop:4,fontWeight:600}}>🚚 {sp.courier} · {sp.tracking_number}</div> : null}
              </div>
              <button data-testid={`ac-sp-edit-${sp.id}`} onClick={() => setEditing(sp)} style={{background:"#111827",color:"#FACC15",border:"none",padding:"8px 14px",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>Manage</button>
            </div>
          ))}
        </div>
      )}
      {editing && <AcSamplePackEditModal sp={editing} onClose={() => setEditing(null)} onDone={async () => { setEditing(null); await load(); }}/>}
    </div>
  );
}


export function AcSamplePackEditModal({ sp, onClose, onDone }) {
  const [status, setStatus] = useState(sp.status);
  const [courier, setCourier] = useState(sp.courier || "");
  const [tracking, setTracking] = useState(sp.tracking_number || "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  // Quote state (visible when status is pending_quote / quoted)
  const [quoteAmount, setQuoteAmount] = useState(sp.quoted_amount_inr || "");
  const [quoteDays, setQuoteDays] = useState(sp.estimated_delivery_days || "");
  const [quoteNote, setQuoteNote] = useState(sp.admin_quote_note || "");

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { status };
      if (courier.trim()) payload.courier = courier.trim();
      if (tracking.trim()) payload.tracking_number = tracking.trim();
      if (note.trim()) payload.admin_note = note.trim();
      await axios.patch(`${API}/admin/sample-packs/${sp.id}`, payload);
      toast.success("Sample pack updated");
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  const submitQuote = async () => {
    const amt = Number(quoteAmount), days = Number(quoteDays);
    if (!amt || amt < 1) return toast.error("Enter a valid quote amount in ₹");
    if (!days || days < 1) return toast.error("Enter a valid delivery days value");
    setBusy(true);
    try {
      await axios.post(`${API}/sample-packs/${sp.id}/quote`, {
        quoted_amount_inr: amt,
        estimated_delivery_days: days,
        admin_quote_note: quoteNote.trim() || "",
      });
      toast.success("Quote sent — buyer notified for payment");
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send quote");
    } finally { setBusy(false); }
  };

  const canQuote = sp.status === "pending_quote" || sp.status === "quoted";

  return (
    <div onClick={onClose} data-testid="ac-sp-modal" style={{position:"fixed",inset:0,background:"rgba(15,23,42,0.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1200,padding:24}}>
      <div onClick={e => e.stopPropagation()} style={{background:"#fff",borderRadius:14,width:560,maxWidth:"96vw",padding:"24px 28px",maxHeight:"92vh",overflow:"auto"}}>
        <h3 style={{margin:"0 0 10px",fontSize:18,fontWeight:700}}>Manage sample pack · #{sp.id.slice(-6)}</h3>
        <p style={{fontSize:12,color:"#6B7280",margin:"0 0 6px"}}>Buyer {sp.buyer_name} · {sp.product_category}</p>
        {sp.intended_use && <p style={{fontSize:11,color:"#475569",margin:"0 0 14px",fontStyle:"italic"}}>Intended use: {sp.intended_use}</p>}

        {canQuote && (
          <div data-testid="ac-sp-quote-panel" style={{background:"#FFFBEB",padding:14,borderRadius:10,border:"1px solid #FDE68A",marginBottom:14}}>
            <h4 style={{margin:"0 0 10px",fontSize:13,fontWeight:800,color:"#92400E"}}>
              {sp.status === "pending_quote" ? "📋 Post a quote (buyer pays after)" : "📋 Revise the active quote"}
            </h4>
            <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:10,marginBottom:10}}>
              <div>
                <label style={{fontSize:11,fontWeight:700,color:"#78350F",letterSpacing:"0.08em",textTransform:"uppercase"}}>Quote amount (₹)</label>
                <input data-testid="ac-sp-quote-amount" type="number" min={1} value={quoteAmount} onChange={e => setQuoteAmount(e.target.value)} placeholder="e.g. 1499" style={{width:"100%",padding:"8px 10px",border:"1px solid #FDE68A",borderRadius:8,fontSize:13,marginTop:6}}/>
              </div>
              <div>
                <label style={{fontSize:11,fontWeight:700,color:"#78350F",letterSpacing:"0.08em",textTransform:"uppercase"}}>Delivery (days)</label>
                <input data-testid="ac-sp-quote-days" type="number" min={1} max={60} value={quoteDays} onChange={e => setQuoteDays(e.target.value)} placeholder="e.g. 5" style={{width:"100%",padding:"8px 10px",border:"1px solid #FDE68A",borderRadius:8,fontSize:13,marginTop:6}}/>
              </div>
            </div>
            <label style={{fontSize:11,fontWeight:700,color:"#78350F",letterSpacing:"0.08em",textTransform:"uppercase"}}>Note to buyer (optional)</label>
            <textarea data-testid="ac-sp-quote-note" value={quoteNote} onChange={e => setQuoteNote(e.target.value.slice(0,600))} rows={2} placeholder="Breakdown, inclusions, delivery expectations…" style={{width:"100%",padding:"8px 10px",border:"1px solid #FDE68A",borderRadius:8,fontSize:13,margin:"6px 0 10px",fontFamily:"inherit",background:"#fff"}}/>
            <button data-testid="ac-sp-quote-submit" onClick={submitQuote} disabled={busy} style={{background:"#92400E",color:"#FEF3C7",border:"none",padding:"8px 18px",borderRadius:8,fontSize:12,fontWeight:800,cursor:"pointer"}}>
              {busy ? "Sending…" : sp.status === "pending_quote" ? "Send quote to buyer" : "Update quote"}
            </button>
          </div>
        )}

        <label style={{fontSize:11,fontWeight:700,color:"#6B7280",letterSpacing:"0.08em",textTransform:"uppercase"}}>Status</label>
        <select value={status} onChange={e => setStatus(e.target.value)} style={{width:"100%",padding:"8px 10px",border:"1px solid #D1D5DB",borderRadius:8,fontSize:13,margin:"6px 0 14px"}}>
          {["pending_quote","quoted","paid","curating","shipped","delivered","rated","cancelled"].map(s => (
            // Disable "quoted" via the dropdown — admin must use the Quote panel
            // above so a Razorpay order is created. Leaves "quoted" selectable
            // (not disabled) only when a quote already exists on the row so an
            // admin can keep it as-is while editing other fields.
            <option key={s} value={s} disabled={s === "quoted" && !sp.quoted_amount_inr}>{s}{s === "quoted" && !sp.quoted_amount_inr ? " · use Quote panel ↑" : ""}</option>
          ))}
        </select>

        <label style={{fontSize:11,fontWeight:700,color:"#6B7280",letterSpacing:"0.08em",textTransform:"uppercase"}}>Courier</label>
        <input value={courier} onChange={e => setCourier(e.target.value)} placeholder="e.g. Delhivery / Bluedart" style={{width:"100%",padding:"8px 10px",border:"1px solid #D1D5DB",borderRadius:8,fontSize:13,margin:"6px 0 14px"}}/>

        <label style={{fontSize:11,fontWeight:700,color:"#6B7280",letterSpacing:"0.08em",textTransform:"uppercase"}}>Tracking number</label>
        <input value={tracking} onChange={e => setTracking(e.target.value)} style={{width:"100%",padding:"8px 10px",border:"1px solid #D1D5DB",borderRadius:8,fontSize:13,margin:"6px 0 14px"}}/>

        <label style={{fontSize:11,fontWeight:700,color:"#6B7280",letterSpacing:"0.08em",textTransform:"uppercase"}}>Admin note (optional)</label>
        <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} style={{width:"100%",padding:"8px 10px",border:"1px solid #D1D5DB",borderRadius:8,fontSize:13,margin:"6px 0 14px",fontFamily:"inherit"}}/>

        {(sp.history || []).length > 0 && (
          <div style={{background:"#F9FAFB",padding:12,borderRadius:10,border:"1px solid #E5E7EB",marginBottom:14}}>
            <div style={{fontSize:11,fontWeight:700,color:"#6B7280",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:6}}>Audit log · last 5</div>
            {sp.history.slice(-5).reverse().map((h, i) => (
              <div key={i} style={{fontSize:11,color:"#374151",padding:"4px 0",borderBottom: i < Math.min(4, sp.history.length-1) ? "1px dashed #E5E7EB" : "none"}}>
                <b>{h.action}</b> {h.amount ? `· ₹${h.amount.toLocaleString("en-IN")}` : ""} {h.actor ? `· by ${h.actor.slice(-6)}` : ""} <span style={{color:"#9CA3AF"}}>· {new Date(h.at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</span>
              </div>
            ))}
          </div>
        )}

        <div style={{display:"flex",justifyContent:"flex-end",gap:10}}>
          <button onClick={onClose} disabled={busy} style={{background:"#F3F4F6",color:"#374151",border:"1px solid #D1D5DB",padding:"9px 16px",borderRadius:8,fontSize:13,fontWeight:600,cursor:"pointer"}}>Cancel</button>
          <button data-testid="ac-sp-save" onClick={submit} disabled={busy} style={{background:"#111827",color:"#FACC15",border:"none",padding:"9px 20px",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer"}}>{busy ? "Saving…" : "Save changes"}</button>
        </div>
      </div>
    </div>
  );
}


// =========================================================================
// ADMIN SETTLEMENTS — monitor, authorize, edit every settlement + audit log
// =========================================================================
export function AcSettlements({ onChange }) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const url = `${API}/admin/settlements${status ? `?status=${status}` : ""}`;
      const { data } = await axios.get(url);
      setData(data);
    } catch (e) { setErr(e?.response?.data?.detail || "Failed to load"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const save = async (patch) => {
    try {
      await axios.patch(`${API}/admin/settlements/${editing.id}`, patch);
      setEditing(null);
      await load();
      onChange && onChange();
    } catch (e) { setErr(e?.response?.data?.detail || "Could not save"); }
  };

  if (!data) return <div className="ac-panel" data-testid="ac-settlements-loading">Loading…</div>;

  const t = data.totals;
  return (
    <div className="ac-panel" data-testid="ac-settlements">
      <div style={{display:"flex",gap:16,flexWrap:"wrap",marginBottom:14}}>
        <StatPill label="Total" value={t.count} testid="ac-stl-total"/>
        <StatPill label="Pending" value={t.pending_count} testid="ac-stl-pending" tone="warn"/>
        <StatPill label="Paid" value={t.paid_count} testid="ac-stl-paid" tone="ok"/>
        <StatPill label="Pending net" value={`₹${t.pending_net_inr?.toLocaleString("en-IN")}`} testid="ac-stl-pendingnet" tone="warn"/>
        <StatPill label="Paid net" value={`₹${t.paid_net_inr?.toLocaleString("en-IN")}`} testid="ac-stl-paidnet" tone="ok"/>
        <StatPill label="TDS collected" value={`₹${t.tds_total_inr?.toLocaleString("en-IN")}`} testid="ac-stl-tds"/>
        <StatPill label="Platform margin" value={`₹${(t.margin_total_inr||0).toLocaleString("en-IN")}`} testid="ac-stl-margin" tone="ok"/>
        <StatPill label="GST on margin" value={`₹${(t.gst_on_margin_total_inr||0).toLocaleString("en-IN")}`} testid="ac-stl-gst-margin"/>
      </div>
      <div style={{display:"flex",gap:6,marginBottom:10}}>
        {["", "pending", "paid", "reversed"].map(s => (
          <button key={s || "all"} data-testid={`ac-stl-filter-${s || "all"}`} onClick={() => setStatus(s)}
            className={status === s ? "verify-pill ok" : "verify-pill pending"} style={{cursor:"pointer",textTransform:"capitalize"}}>
            {s || "all"}
          </button>
        ))}
      </div>

      {data.settlements.length === 0 ? (
        <div className="empty" data-testid="ac-stl-empty">No settlements match this filter.</div>
      ) : (
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",fontSize:12,borderCollapse:"collapse"}}>
            <thead>
              <tr style={{background:"#f3f4f6",textAlign:"left"}}>
                <th style={th}>Settlement #</th><th style={th}>Status</th><th style={th}>Supplier</th><th style={th}>Job</th>
                <th style={{...th,textAlign:"right"}}>Gross</th><th style={{...th,textAlign:"right"}}>Commission</th>
                <th style={{...th,textAlign:"right"}}>GST</th><th style={{...th,textAlign:"right"}}>TDS</th>
                <th style={{...th,textAlign:"right"}}>Net wire</th><th style={{...th,textAlign:"right"}}>Margin</th><th style={{...th,textAlign:"right"}}>GST on margin</th><th style={th}>UTR</th><th style={th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.settlements.map(s => (
                <tr key={s.id} data-testid={`ac-stl-row-${s.id}`} style={{borderBottom:"1px solid #e5e7eb"}}>
                  <td style={td}><b>{s.settlement_number || "—"}</b><div style={{fontSize:10,color:"#9ca3af"}}>{s.created_at?.slice(0,10)}</div></td>
                  <td style={td}><span className={`verify-pill ${s.status === "paid" ? "ok" : "pending"}`}>{s.status}</span></td>
                  <td style={td}>{s.supplier_name}</td>
                  <td style={td}>{s.job_title}</td>
                  <td style={{...td,textAlign:"right"}}>{s.gross_inr != null ? `₹${s.gross_inr.toLocaleString("en-IN")}` : "—"}</td>
                  <td style={{...td,textAlign:"right",color:"#9ca3af"}}>{s.platform_commission_inr != null ? `−₹${s.platform_commission_inr.toLocaleString("en-IN")}` : "—"}</td>
                  <td style={{...td,textAlign:"right",color:"#9ca3af"}}>{s.gst_amount_inr != null ? <>₹{s.gst_amount_inr.toLocaleString("en-IN")}<div style={{fontSize:10}}>@ {s.gst_rate_pct}%</div></> : "—"}</td>
                  <td style={{...td,textAlign:"right",color:"#B45309"}}>{s.tds_amount_inr != null ? `−₹${s.tds_amount_inr.toLocaleString("en-IN")}` : "—"}</td>
                  <td style={{...td,textAlign:"right",color:"#065F46",fontWeight:600}}>{s.net_payable_inr != null ? `₹${s.net_payable_inr.toLocaleString("en-IN")}` : "—"}</td>
                  <td data-testid={`ac-stl-margin-${s.id}`} style={{...td,textAlign:"right",color:"#1E3A8A"}}>{s.margin ? `₹${s.margin.margin_taxable_inr.toLocaleString("en-IN")}` : "—"}</td>
                  <td data-testid={`ac-stl-gst-margin-${s.id}`} style={{...td,textAlign:"right",color:"#475569"}}>{s.margin ? <>₹{s.margin.net_gst_on_margin_inr.toLocaleString("en-IN")}<div style={{fontSize:10,color:"#9ca3af"}}>buyer-borne</div></> : "—"}{s.gst_adjustment_due_inr ? <div data-testid={`ac-stl-adj-${s.id}`} style={{fontSize:10,color:"#B45309"}}>Adj. due {s.gst_adjustment_due_inr < 0 ? "−" : "+"}₹{Math.abs(s.gst_adjustment_due_inr).toLocaleString("en-IN")}</div> : null}</td>
                  <td style={td}>{s.utr ? <code style={{fontSize:11}}>{s.utr}</code> : <span style={{color:"#9ca3af"}}>—</span>}</td>
                  <td style={td}>
                    <a data-testid={`ac-stl-pdf-${s.id}`} href={`${API}/settlements/${s.id}/pdf`} target="_blank" rel="noopener noreferrer" className="secondary-btn" style={{textDecoration:"none",fontSize:11,padding:"4px 10px",marginRight:4}}>PDF</a>
                    <button data-testid={`ac-stl-edit-${s.id}`} className="primary-btn" style={{fontSize:11,padding:"4px 10px"}} onClick={() => setEditing({...s})}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {err && <div className="form-error" data-testid="ac-stl-error" style={{marginTop:10}}>{err}</div>}
      {editing && <SettlementEditModal row={editing} onClose={() => setEditing(null)} onSave={save}/>}
    </div>
  );
}

export function SettlementEditModal({ row, onClose, onSave }) {
  const [status, setStatus] = useState(row.status || "pending");
  const [utr, setUtr] = useState(row.utr || "");
  const [method, setMethod] = useState(row.method || "neft");
  const [paidAmount, setPaidAmount] = useState(row.paid_amount_inr ?? row.net_payable_inr ?? 0);
  const [paidAt, setPaidAt] = useState(row.paid_at ? row.paid_at.slice(0, 10) : "");
  const [note, setNote] = useState(row.note || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await onSave({
        status,
        utr: utr || null,
        method,
        paid_amount_inr: Number(paidAmount) || null,
        paid_at: paidAt ? `${paidAt}T00:00:00Z` : null,
        note: note || null,
      });
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-shell" data-testid="settlement-edit-modal" onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
      <div onClick={e => e.stopPropagation()} style={{background:"#fff",padding:24,borderRadius:12,width:560,maxWidth:"95vw",maxHeight:"90vh",overflow:"auto"}}>
        <h3 style={{margin:0,marginBottom:4}}>Edit settlement</h3>
        <p style={{fontSize:12,color:"#6b7280",marginBottom:14}}>
          <b>{row.settlement_number}</b> · {row.supplier_name} · {row.job_title} · Net wire ₹{row.net_payable_inr?.toLocaleString("en-IN")}
        </p>

        <div style={{display:"grid",gap:12}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <label style={{fontSize:12,color:"#6b7280"}}>Status
              <select data-testid="stl-edit-status" value={status} onChange={e => setStatus(e.target.value)} style={inp}>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="reversed">Reversed</option>
              </select>
            </label>
            <label style={{fontSize:12,color:"#6b7280"}}>Method
              <select data-testid="stl-edit-method" value={method} onChange={e => setMethod(e.target.value)} style={inp}>
                <option value="neft">NEFT</option><option value="imps">IMPS</option>
                <option value="rtgs">RTGS</option><option value="upi">UPI</option>
                <option value="razorpayx">RazorpayX</option>
              </select>
            </label>
          </div>
          <label style={{fontSize:12,color:"#6b7280"}}>UTR / Transaction reference
            <input data-testid="stl-edit-utr" value={utr} onChange={e => setUtr(e.target.value)} placeholder="e.g. HDFC2026100500001" style={inp}/>
          </label>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <label style={{fontSize:12,color:"#6b7280"}}>Paid amount (INR)
              <input data-testid="stl-edit-amount" type="number" value={paidAmount} onChange={e => setPaidAmount(e.target.value)} style={inp}/>
            </label>
            <label style={{fontSize:12,color:"#6b7280"}}>Paid on
              <input data-testid="stl-edit-paidat" type="date" value={paidAt} onChange={e => setPaidAt(e.target.value)} style={inp}/>
            </label>
          </div>
          <label style={{fontSize:12,color:"#6b7280"}}>Note
            <textarea data-testid="stl-edit-note" value={note} onChange={e => setNote(e.target.value)} rows={2} style={inp}/>
          </label>
        </div>

        {row.history && row.history.length > 0 && (
          <div style={{marginTop:14,padding:12,background:"#F9FAFB",borderRadius:8,fontSize:11}}>
            <b style={{fontSize:12}}>Audit log ({row.history.length})</b>
            <ul style={{marginTop:6,paddingLeft:16,lineHeight:1.7}} data-testid="stl-edit-history">
              {row.history.slice().reverse().slice(0, 6).map((h, i) => (
                <li key={i}><code>{h.action}</code> at {h.at?.slice(0, 19)} by {h.by_admin_email}{h.utr && <> · UTR {h.utr}</>}</li>
              ))}
            </ul>
          </div>
        )}

        <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:16}}>
          <button data-testid="stl-edit-cancel" className="secondary-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button data-testid="stl-edit-save" className="primary-btn" onClick={submit} disabled={busy}>{busy ? "Saving…" : "Save changes"}</button>
        </div>
      </div>
    </div>
  );
}


// =========================================================================
// SUPPLIER SETTLEMENTS — view-only summary + download settlement sheets

export function AcPayments({ onChange }) {
  const [status, setStatus] = useState("In Escrow");
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  const [reasons, setReasons] = useState({});
  const [refundAmt, setRefundAmt] = useState({});
  const [err, setErr] = useState("");
  const [confirmRefund, setConfirmRefund] = useState(null); // {id, gross, remaining}

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/payments${status ? `?status=${encodeURIComponent(status)}` : ""}`);
      setRows(data.payments);
    } catch { setRows([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const act = async (id, action) => {
    setBusy(id); setErr("");
    try {
      await axios.post(`${API}/admin/payments/${id}/${action}`, { reason: reasons[id] || null });
      setReasons(r => ({ ...r, [id]: "" }));
      await load();
      onChange?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not update payment.");
    } finally { setBusy(""); }
  };

  const doRefund = async () => {
    if (!confirmRefund) return;
    const id = confirmRefund.id;
    const amt = parseFloat(refundAmt[id] || confirmRefund.remaining);
    setBusy(id); setErr("");
    try {
      await axios.post(`${API}/admin/payments/${id}/refund`, {
        amount_inr: amt >= confirmRefund.remaining ? null : amt,
        reason: reasons[id] || null,
      });
      setReasons(r => ({ ...r, [id]: "" }));
      setRefundAmt(r => ({ ...r, [id]: "" }));
      setConfirmRefund(null);
      await load();
      onChange?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Refund failed.");
    } finally { setBusy(""); }
  };

  if (!rows) return <div className="ac-panel" data-testid="ac-payments-loading">Loading…</div>;

  const canRefund = (p) => p.gateway === "razorpay" && p.razorpay_payment_id && p.payment_status !== "Released";

  return (
    <div className="ac-panel" data-testid="ac-payments">
      <header className="ac-panel-head">
        <b>Escrow payments</b>
        <div className="ac-controls">
          {["In Escrow", "On Hold", "Refunded", "Partially Refunded", "Released", ""].map(s => (
            <button key={s} data-testid={`ac-pay-filter-${s || "all"}`} className={`chip ${status === s ? "active" : ""}`} onClick={() => setStatus(s)}>
              {s || "All"}
            </button>
          ))}
        </div>
      </header>
      {rows.length === 0 ? (
        <div className="ac-panel empty" data-testid="ac-payments-empty">No payments in this bucket.</div>
      ) : (
        <table className="ac-table" data-testid="ac-payments-table">
          <thead>
            <tr>
              <th>Payment</th>
              <th>Job</th>
              <th>Amount</th>
              <th>Refunded</th>
              <th>Gateway</th>
              <th>Status</th>
              <th style={{ minWidth: 320 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(p => {
              const refunded = Number(p.refunded_total_inr || 0);
              const remaining = Math.max(Number(p.gross || 0) - refunded, 0);
              return (
                <tr key={p.id} data-testid={`ac-pay-row-${p.id}`}>
                  <td className="mono">{p.id}</td>
                  <td className="mono">{p.job_id || "—"}</td>
                  <td>{money(p.gross || 0)}</td>
                  <td>{refunded > 0 ? <span className="text-warn">{money(refunded)}</span> : <span className="muted">—</span>}</td>
                  <td>{p.gateway || "—"}</td>
                  <td><span className={`chip status-${(p.payment_status || "").replace(/\s+/g, "-").toLowerCase()}`}>{p.payment_status || "—"}</span></td>
                  <td>
                    <div className="ac-pay-actions">
                      <input
                        data-testid={`ac-pay-reason-${p.id}`}
                        placeholder="Reason (optional)"
                        value={reasons[p.id] || ""}
                        onChange={e => setReasons(r => ({ ...r, [p.id]: e.target.value }))}
                        disabled={busy === p.id}
                      />
                      {p.payment_status === "On Hold" && (
                        <button data-testid={`ac-pay-release-${p.id}`} className="primary-btn" disabled={busy === p.id} onClick={() => act(p.id, "release")}>Release</button>
                      )}
                      {p.payment_status === "In Escrow" && (
                        <>
                          <button data-testid={`ac-pay-hold-${p.id}`} className="secondary-btn" disabled={busy === p.id} onClick={() => act(p.id, "hold")}>Hold</button>
                          <button data-testid={`ac-pay-release-${p.id}`} className="primary-btn" disabled={busy === p.id} onClick={() => act(p.id, "release")}>Release</button>
                        </>
                      )}
                      {canRefund(p) && remaining > 0 && (
                        <button
                          data-testid={`ac-pay-refund-${p.id}`}
                          className="secondary-btn danger"
                          disabled={busy === p.id}
                          onClick={() => setConfirmRefund({ id: p.id, gross: Number(p.gross || 0), remaining, jobId: p.job_id })}
                        >
                          Refund
                        </button>
                      )}
                      {p.payment_status === "Released" && (
                        <span className="muted small">Released {p.released_at ? new Date(p.released_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" }) : ""}</span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {err && <div className="form-error" data-testid="ac-pay-error">{err}</div>}

      {confirmRefund && (
        <div className="modal-backdrop" data-testid="ac-refund-modal" onClick={() => setConfirmRefund(null)}>
          <div className="modal-card" onClick={e => e.stopPropagation()}>
            <header>
              <b>Refund payment</b>
              <button data-testid="ac-refund-close" className="icon-btn" onClick={() => setConfirmRefund(null)}><X size={16}/></button>
            </header>
            <p className="modal-sub">
              Buyer will be refunded via the original payment method (T+5 to bank for cards / instant to UPI wallets).
              This action cannot be reversed.
            </p>
            <div className="modal-body">
              <label>
                Amount to refund (₹)
                <input
                  data-testid="ac-refund-amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={confirmRefund.remaining}
                  value={refundAmt[confirmRefund.id] ?? confirmRefund.remaining}
                  onChange={e => setRefundAmt(r => ({ ...r, [confirmRefund.id]: e.target.value }))}
                />
                <span className="muted small">Max refundable: {money(confirmRefund.remaining)}</span>
              </label>
              <label>
                Reason (shared with buyer)
                <input
                  data-testid="ac-refund-reason"
                  placeholder="e.g. Job cancelled by buyer before production"
                  value={reasons[confirmRefund.id] || ""}
                  onChange={e => setReasons(r => ({ ...r, [confirmRefund.id]: e.target.value }))}
                />
              </label>
            </div>
            <footer>
              <button className="secondary-btn" onClick={() => setConfirmRefund(null)} data-testid="ac-refund-cancel">Cancel</button>
              <button data-testid="ac-refund-confirm" className="primary-btn danger" disabled={busy === confirmRefund.id} onClick={doRefund}>
                {busy === confirmRefund.id ? "Refunding…" : `Refund ${money(parseFloat(refundAmt[confirmRefund.id] ?? confirmRefund.remaining))}`}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}


export function AcPlatformStats() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [incTest, setIncTest] = useState(false);
  useEffect(() => {
    axios.get(`${API}/admin/platform-stats`, { params: { days, include_test: incTest } }).then(r => setData(r.data)).catch(() => setData({ series: [], total_gmv: 0, total_revenue: 0 }));
  }, [days, incTest]);
  if (!data) return <div className="ac-panel" data-testid="ac-stats-loading">Loading…</div>;
  const maxGmv = Math.max(1, ...data.series.map(s => s.gmv));
  return (
    <div className="ac-panel" data-testid="ac-stats">
      <header className="ac-panel-head">
        <b>Revenue & GMV · last {days} days</b>
        <div className="ac-controls">
          <IncludeTestToggle value={incTest} onChange={setIncTest} count={data.test_orders} testid="stats-include-test"/>
          {[7, 30, 90].map(d => (
            <button key={d} data-testid={`ac-stats-days-${d}`} className={`chip ${days === d ? "active" : ""}`} onClick={() => setDays(d)}>{d}d</button>
          ))}
        </div>
      </header>
      <div className="ac-stat-strip">
        <div><b>Total GMV</b><span>{money(data.total_gmv)}</span></div>
        <div><b>Platform revenue</b><span>{money(data.total_revenue)}</span></div>
        <div><b>Take-rate</b><span>{data.platform_markup_pct}%</span></div>
      </div>
      <div className="ac-chart" data-testid="ac-stats-chart">
        {data.series.map(s => (
          <div key={s.date} className="ac-bar dual" data-testid={`ac-stats-bar-${s.date}`}>
            <div className="ac-bar-fill" style={{ height: `${(s.gmv / maxGmv) * 100}%` }} title={`${s.date} · GMV ${s.gmv}`}/>
            <div className="ac-bar-fill accent" style={{ height: `${(s.revenue / maxGmv) * 100}%` }} title={`${s.date} · Revenue ${s.revenue}`}/>
            <span className="ac-bar-date">{s.date.slice(5)}</span>
          </div>
        ))}
      </div>
      <p className="ac-legend"><span className="dot"/> GMV · <span className="dot accent"/> Platform revenue ({data.platform_markup_pct}% take-rate)</p>
    </div>
  );
}
